/**
 * AuthorPlatformService — CRUD for per-author Platform docs at:
 *   projects/{pid}/{authors|external_authors}/{authorId}/platforms/{platformId}
 *
 * The `basePath` constructor option selects between self-author
 * (`authors` — default) and tracked-creator (`external_authors`) collections.
 * Two prebuilt singletons are exported:
 *   - `authorPlatformService` (default, basePath='authors')
 *   - `externalAuthorPlatformService` (basePath='external_authors')
 * Pick the right one in components via the `basePath` prop.
 *
 * Companion to AuthorOnboardingHandler — that handler creates the initial
 * platform during onboarding (via backend BD scrape). This service handles
 * platform-level operations that don't need backend AI:
 *
 *   addPlatformManual    → create empty platform from system catalog (no scrape)
 *   updatePlatform       → patch any subset of canonical Platform fields
 *   enablePlatform       → flip isActive back to true
 *   disablePlatform      → soft-delete (isActive=false, preserves nested data)
 *   getSystemPlatforms   → list catalog options for AddPlatformDialog dropdown
 *
 * Schema: canonical Platform from openapi-content-plan.yaml (1.3.1+).
 * All writes use stripUndefined + serverTimestamp.
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  writeBatch,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { CoreFirebaseService } from '@modules/firebase-core/services/CoreFirebaseService';
import { stripUndefined } from '@modules/content-plan/utils/stripUndefined';
import type {
  Platform,
  PlatformDesignProfile,
  PlatformDesignProfileScope,
  PlatformDesignProfileSet,
} from '@segmently/schema-models/content-plan';
import {
  systemPlatformService,
  type SystemBrandStyleKitProfile,
} from '@modules/content-plan/services/SystemPlatformService';

/**
 * Subset of Platform fields user can edit from the UI editor.
 * Excluded from update: id, platformId (immutable identifiers), enabledAt,
 * createdAt (audit fields). updatedAt is set automatically.
 */
export type PlatformPatch = Partial<
  Omit<Platform, 'id' | 'platformId' | 'enabledAt'>
>;

/** Lite shape returned by getSystemPlatforms — for AddPlatformDialog dropdown. */
export interface SystemPlatformLite {
  id: string;
  platformId: string;
  displayName: string;
  icon?: string;
  hasAlgorithm?: boolean;
  isActive?: boolean;
  maxPostLength?: number;
  optimalPostFrequency?: { min?: number; max?: number };
  optimalTimes?: Array<string | { day?: string | number; hour?: number; score?: number }>;
  optimalSlots?: Array<{ day?: string | number; timeLocal?: string; score?: number }>;
  promptRef?: string;
}

/** Discriminator selecting which Author collection this service operates on. */
export type AuthorBasePath = 'authors' | 'external_authors';

export interface AuthorDesignProfileSet extends Omit<PlatformDesignProfileSet, 'profileKey'> {
  profileKey: PlatformDesignProfileScope | string;
  profiles?: AuthorDesignProfile[];
  [key: string]: unknown;
}

export interface AuthorDesignProfile extends PlatformDesignProfile {
  id: string;
  [key: string]: unknown;
}

export type DesignSystemPackageStatus = 'draft' | 'reviewed' | 'archived';

// NOTE: built from PlatformDesignProfile (not AuthorDesignProfile) because
// Omit<> over a type with a string index signature erases all named keys.
export type AuthorDesignSystemPackageProfile = Omit<PlatformDesignProfile, 'profileKey'> & {
  id: string;
  packageId: string;
  profileKey: PlatformDesignProfileScope | string;
  contentHash?: string | null;
  [key: string]: unknown;
};

export interface AuthorDesignSystemPackage {
  id: string;
  packageId: string;
  name?: string;
  description?: string;
  status?: DesignSystemPackageStatus;
  targetProfileKeys?: string[];
  completeProfileKeys?: string[];
  profileCount?: number;
  visualFoundation?: Record<string, unknown>;
  contentHash?: string | null;
  current?: boolean;
  profiles?: AuthorDesignSystemPackageProfile[];
  [key: string]: unknown;
}

export type AuthorDesignSystemPackageProfileInput =
  Partial<AuthorDesignSystemPackageProfile>
  & {
    profileKey: PlatformDesignProfileScope | string;
    id?: string;
    packageId?: string;
    contentHash?: string | null;
  };

export interface AuthorDesignSystemPackageSaveInput {
  id?: string;
  packageId?: string;
  name?: string;
  description?: string;
  status?: DesignSystemPackageStatus;
  targetProfileKeys?: string[];
  visualFoundation?: Record<string, unknown>;
  contentHash?: string | null;
  profiles: AuthorDesignSystemPackageProfileInput[];
  [key: string]: unknown;
}

class AuthorPlatformService {
  constructor(private readonly basePath: AuthorBasePath = 'authors') {}

  private get db() {
    return CoreFirebaseService.getInstance().firestore;
  }

  private platformDocRef(projectId: string, authorId: string, platformId: string) {
    return doc(
      this.db,
      'projects',
      projectId,
      this.basePath,
      authorId,
      'platforms',
      platformId,
    );
  }

  private platformsCollectionRef(projectId: string, authorId: string) {
    return collection(
      this.db,
      'projects',
      projectId,
      this.basePath,
      authorId,
      'platforms',
    );
  }

  private designProfileSetCollectionRef(projectId: string, authorId: string, platformId: string) {
    return collection(
      this.platformDocRef(projectId, authorId, platformId),
      'design_profile_sets',
    );
  }

  private designProfileSetDocRef(
    projectId: string,
    authorId: string,
    platformId: string,
    profileKey: string,
  ) {
    return doc(
      this.platformDocRef(projectId, authorId, platformId),
      'design_profile_sets',
      profileKey,
    );
  }

  private designProfileCollectionRef(
    projectId: string,
    authorId: string,
    platformId: string,
    profileKey: string,
  ) {
    return collection(
      this.designProfileSetDocRef(projectId, authorId, platformId, profileKey),
      'profiles',
    );
  }

  private designProfileDocRef(
    projectId: string,
    authorId: string,
    platformId: string,
    profileKey: string,
    profileId: string,
  ) {
    return doc(
      this.designProfileSetDocRef(projectId, authorId, platformId, profileKey),
      'profiles',
      profileId,
    );
  }

  private runtimeDesignDocRef(
    projectId: string,
    authorId: string,
    platformId: string,
    profileKey: string,
  ) {
    return doc(this.platformDocRef(projectId, authorId, platformId), 'design', profileKey);
  }

  private designSystemPackageCollectionRef(projectId: string, authorId: string, platformId: string) {
    return collection(this.platformDocRef(projectId, authorId, platformId), 'design_system_packages');
  }

  private designSystemPackageDocRef(
    projectId: string,
    authorId: string,
    platformId: string,
    packageId: string,
  ) {
    return doc(this.platformDocRef(projectId, authorId, platformId), 'design_system_packages', packageId);
  }

  private designSystemPackageProfileCollectionRef(
    projectId: string,
    authorId: string,
    platformId: string,
    packageId: string,
  ) {
    return collection(this.designSystemPackageDocRef(projectId, authorId, platformId, packageId), 'profiles');
  }

  private designSystemPackageProfileDocRef(
    projectId: string,
    authorId: string,
    platformId: string,
    packageId: string,
    profileKey: string,
  ) {
    return doc(this.designSystemPackageDocRef(projectId, authorId, platformId, packageId), 'profiles', profileKey);
  }

  /**
   * Add a platform from the system catalog WITHOUT triggering a BD scrape.
   * Use when the user wants to set up a manual-paste workflow on a new
   * platform (e.g. paste posts manually into Twitter platform after LinkedIn
   * onboarding). For URL-mode add-platform that scrapes posts via BrightData,
   * use AuthorOnboardingHandler with mode='add-platform' instead.
   *
   * Copies all fields from `system_platforms/{platformId}` plus its
   * `formats/*` subcollection and stamps `enabledAt`/`updatedAt`. Does NOT
   * create voice/post_examples — those are AI-generated only. UI shows empty
   * Voice tab until user clicks Re-analyze.
   */
  async addPlatformManual(opts: {
    projectId: string;
    authorId: string;
    platformId: string;
    profileUrl?: string;
  }): Promise<void> {
    const { projectId, authorId, platformId, profileUrl } = opts;
    const sysRef = doc(this.db, 'system_platforms', platformId);
    const sysSnap = await getDoc(sysRef);
    if (!sysSnap.exists()) {
      throw new Error(
        `System platform "${platformId}" is not configured in system_platforms. Add the platform in Admin → System Platforms before enabling it for an author.`,
      );
    }

    const sysData = sysSnap.data();
    if (sysData.isActive === false) {
      throw new Error(`System platform "${platformId}" is inactive and cannot be enabled for authors.`);
    }
    const formatsSnap = await getDocs(collection(this.db, 'system_platforms', platformId, 'formats'));
    if (formatsSnap.empty) {
      throw new Error(`System platform "${platformId}" must define at least one formats/* document before it can be enabled.`);
    }
    const [templatesSnap, brandKitSetsSnap] = await Promise.all([
      getDocs(collection(this.db, 'system_platforms', platformId, 'post_templates')),
      getDocs(collection(this.db, 'system_platforms', platformId, 'brand_style_kit')),
    ]);
    const now = serverTimestamp() as any;
    const platformDoc = stripUndefined({
      ...sysData,
      id: platformId,
      platformId,
      profileUrl,
      isActive: true,
      enabledAt: now,
      updatedAt: now,
    }) as Record<string, unknown>;

    const batch = writeBatch(this.db);
    const platformRef = this.platformDocRef(projectId, authorId, platformId);
    batch.set(platformRef, platformDoc);
    formatsSnap.docs.forEach((formatDoc) => {
      batch.set(
        doc(platformRef, 'formats', formatDoc.id),
        stripUndefined({
          ...formatDoc.data(),
          createdAt: now,
          updatedAt: now,
        }) as Record<string, unknown>,
      );
    });
    templatesSnap.docs.forEach((templateDoc) => {
      batch.set(
        doc(platformRef, 'post_templates', templateDoc.id),
        stripUndefined({
          ...templateDoc.data(),
          scope: 'author',
          authorId,
          platformId,
          baseTemplateId: templateDoc.id,
          createdAt: now,
          updatedAt: now,
        }) as Record<string, unknown>,
      );
    });
    for (const setDoc of brandKitSetsSnap.docs) {
      const setData = setDoc.data();
      const profileKey = setDoc.id;
      const profilesSnap = await getDocs(collection(setDoc.ref, 'profiles'));
      const profiles = profilesSnap.docs.map((profileDoc) => ({
        id: profileDoc.id,
        data: profileDoc.data(),
      }));
      const currentProfileId = String(setData.currentProfileId ?? profiles[0]?.id ?? '').trim();
      batch.set(
        doc(platformRef, 'design_profile_sets', profileKey),
        stripUndefined({
          ...setData,
          profileKey,
          currentProfileId,
          updatedAt: now,
        }) as Record<string, unknown>,
      );
      profiles.forEach((profile) => {
        batch.set(
          doc(platformRef, 'design_profile_sets', profileKey, 'profiles', profile.id),
          stripUndefined({
            ...profile.data,
            profileId: String((profile.data as any).profileId ?? profile.id),
            profileKey,
            platformId,
            authorId,
            source: 'copied_from',
            copiedFromPlatformId: platformId,
            copiedFromProfileId: String((profile.data as any).profileId ?? profile.id),
            copiedFromProfileKey: profileKey,
            updatedAt: now,
          }) as Record<string, unknown>,
        );
      });
      const active = profiles.find((profile) => profile.id === currentProfileId) ?? profiles[0];
      if (active) {
        batch.set(
          doc(platformRef, 'design', profileKey),
          stripUndefined({
            ...active.data,
            profileId: String((active.data as any).profileId ?? active.id),
            profileKey,
            platformId,
            authorId,
            source: 'copied_from',
            copiedFromPlatformId: platformId,
            copiedFromProfileId: String((active.data as any).profileId ?? active.id),
            copiedFromProfileKey: profileKey,
            updatedAt: now,
          }) as Record<string, unknown>,
        );
      }
    }
    await batch.commit();
  }

  /**
   * Patch any subset of canonical Platform fields. Used by
   * PlatformConfigEditor when user edits maxPostLength, optimalTimes, etc.
   * Always bumps updatedAt.
   */
  async updatePlatform(opts: {
    projectId: string;
    authorId: string;
    platformId: string;
    patch: PlatformPatch;
  }): Promise<void> {
    const stripped = stripUndefined({
      ...opts.patch,
      updatedAt: serverTimestamp() as any,
    }) as Record<string, unknown>;
    await updateDoc(
      this.platformDocRef(opts.projectId, opts.authorId, opts.platformId),
      stripped,
    );
  }

  /** Convenience: flip isActive=true. */
  async enablePlatform(opts: {
    projectId: string;
    authorId: string;
    platformId: string;
  }): Promise<void> {
    await this.updatePlatform({ ...opts, patch: { isActive: true } });
  }

  /**
   * Soft-delete: isActive=false. Nested data (voice, post_examples,
   * author_posts, post_templates) preserved — re-enabling restores state.
   */
  async disablePlatform(opts: {
    projectId: string;
    authorId: string;
    platformId: string;
  }): Promise<void> {
    await this.updatePlatform({ ...opts, patch: { isActive: false } });
  }

  /**
   * Read existing platforms list for the author. Used by AddPlatformDialog
   * to filter the system catalog dropdown — only platforms NOT yet enabled
   * appear as options.
   */
  async listEnabledPlatformIds(opts: {
    projectId: string;
    authorId: string;
  }): Promise<string[]> {
    const snap = await getDocs(
      this.platformsCollectionRef(opts.projectId, opts.authorId),
    );
    return snap.docs.map((d) => d.id);
  }

  /**
   * Read active system catalog. Used as dropdown source in AddPlatformDialog
   * and strategy pickers; the catalog is the product source of truth.
   */
  async getSystemPlatforms(): Promise<SystemPlatformLite[]> {
    return systemPlatformService.listPlatforms({ activeOnly: true });
  }

  async listDesignProfileSets(opts: {
    projectId: string;
    authorId: string;
    platformId: string;
    includeProfiles?: boolean;
  }): Promise<AuthorDesignProfileSet[]> {
    const snap = await getDocs(
      this.designProfileSetCollectionRef(opts.projectId, opts.authorId, opts.platformId),
    );
    const sets = snap.docs
      .map((d) => ({ ...(d.data() as PlatformDesignProfileSet), profileKey: d.id } as AuthorDesignProfileSet))
      .filter((set) => set.isActive !== false)
      .sort((a, b) => String(a.displayName ?? a.profileKey).localeCompare(String(b.displayName ?? b.profileKey)));
    if (opts.includeProfiles !== true) return sets;
    return Promise.all(
      sets.map(async (set) => ({
        ...set,
        profiles: await this.listDesignProfiles({
          projectId: opts.projectId,
          authorId: opts.authorId,
          platformId: opts.platformId,
          profileKey: String(set.profileKey),
        }),
      })),
    );
  }

  async listDesignProfiles(opts: {
    projectId: string;
    authorId: string;
    platformId: string;
    profileKey: string;
  }): Promise<AuthorDesignProfile[]> {
    const snap = await getDocs(
      this.designProfileCollectionRef(opts.projectId, opts.authorId, opts.platformId, opts.profileKey),
    );
    return snap.docs
      .map((d) => ({ ...(d.data() as PlatformDesignProfile), id: d.id } as AuthorDesignProfile))
      .sort((a, b) => String(a.name ?? a.id).localeCompare(String(b.name ?? b.id)));
  }

  async listDesignSystemPackages(opts: {
    projectId: string;
    authorId: string;
    platformId: string;
    includeProfiles?: boolean;
  }): Promise<AuthorDesignSystemPackage[]> {
    const [platformSnap, packagesSnap] = await Promise.all([
      getDoc(this.platformDocRef(opts.projectId, opts.authorId, opts.platformId)),
      getDocs(this.designSystemPackageCollectionRef(opts.projectId, opts.authorId, opts.platformId)),
    ]);
    const currentPackageId = String(platformSnap.data()?.currentDesignSystemPackageId ?? '').trim();
    const packages = packagesSnap.docs
      .map((d) => {
        const data = d.data() as AuthorDesignSystemPackage;
        return {
          ...data,
          id: d.id,
          packageId: String(data.packageId ?? d.id),
          current: currentPackageId ? d.id === currentPackageId : data.current === true,
        } as AuthorDesignSystemPackage;
      })
      .filter((item) => item.status !== 'archived')
      .sort((a, b) => {
        if (a.current !== b.current) return a.current ? -1 : 1;
        return String(a.name ?? a.packageId).localeCompare(String(b.name ?? b.packageId));
      });
    if (opts.includeProfiles !== true) return packages;
    return Promise.all(
      packages.map(async (item) => ({
        ...item,
        profiles: await this.listDesignSystemPackageProfiles({
          projectId: opts.projectId,
          authorId: opts.authorId,
          platformId: opts.platformId,
          packageId: item.packageId,
        }),
      })),
    );
  }

  async listDesignSystemPackageProfiles(opts: {
    projectId: string;
    authorId: string;
    platformId: string;
    packageId: string;
  }): Promise<AuthorDesignSystemPackageProfile[]> {
    const packageId = normalizeDesignSystemPackageId(opts.packageId, 'packageId');
    const snap = await getDocs(
      this.designSystemPackageProfileCollectionRef(opts.projectId, opts.authorId, opts.platformId, packageId),
    );
    return snap.docs
      .map((d) => ({
        ...(d.data() as PlatformDesignProfile),
        id: d.id,
        packageId,
        profileKey: String((d.data() as any).profileKey ?? d.id),
      } as AuthorDesignSystemPackageProfile))
      .sort((a, b) => profileKeySortIndex(String(a.profileKey)) - profileKeySortIndex(String(b.profileKey)));
  }

  async saveDesignSystemPackage(opts: {
    projectId: string;
    authorId: string;
    platformId: string;
    designPackage: AuthorDesignSystemPackageSaveInput;
    setCurrent?: boolean;
  }): Promise<void> {
    const packageId = normalizeDesignSystemPackageId(
      opts.designPackage.packageId ?? opts.designPackage.id,
      'packageId',
    );
    const profiles = normalizeDesignSystemPackageProfiles(opts.designPackage.profiles, packageId);
    if (profiles.length === 0) {
      throw new Error('At least one design system package profile is required.');
    }
    const targetProfileKeys = uniqueStrings([
      ...(opts.designPackage.targetProfileKeys ?? []),
      ...profiles.map((profile) => String(profile.profileKey)),
    ]);
    const completeProfileKeys = uniqueStrings(
      profiles
        .filter((profile) => profile.isComplete !== false)
        .map((profile) => String(profile.profileKey)),
    );
    const now = serverTimestamp() as any;
    const batch = writeBatch(this.db);
    batch.set(
      this.designSystemPackageDocRef(opts.projectId, opts.authorId, opts.platformId, packageId),
      stripUndefined({
        ...opts.designPackage,
        id: undefined,
        profiles: undefined,
        packageId,
        name: opts.designPackage.name ?? packageId,
        status: opts.designPackage.status ?? 'draft',
        targetProfileKeys,
        completeProfileKeys,
        profileCount: profiles.length,
        current: opts.setCurrent === true ? true : opts.designPackage.current,
        contentHash: opts.designPackage.contentHash ?? null,
        updatedAt: now,
      }) as Record<string, unknown>,
      { merge: true },
    );
    profiles.forEach((profile) => {
      batch.set(
        this.designSystemPackageProfileDocRef(
          opts.projectId,
          opts.authorId,
          opts.platformId,
          packageId,
          String(profile.profileKey),
        ),
        stripUndefined({
          ...profilePayloadForAuthor(profile, {
            platformId: opts.platformId,
            authorId: opts.authorId,
            profileKey: String(profile.profileKey),
            profileId: String(profile.profileId ?? packageId),
            source: 'package_profile',
          }),
          packageId,
          contentHash: profile.contentHash ?? null,
          designPackageContentHash: opts.designPackage.contentHash ?? null,
          designPackageProfileContentHash: profile.contentHash ?? null,
          updatedAt: now,
        }) as Record<string, unknown>,
        { merge: true },
      );
      if (opts.setCurrent === true) {
        this.materializeDesignSystemPackageProfile(batch, {
          projectId: opts.projectId,
          authorId: opts.authorId,
          platformId: opts.platformId,
          packageId,
          packageName: opts.designPackage.name ?? packageId,
          packageContentHash: opts.designPackage.contentHash,
          profile,
          now,
        });
      }
    });
    if (opts.setCurrent === true) {
      batch.set(
        this.platformDocRef(opts.projectId, opts.authorId, opts.platformId),
        stripUndefined({
          currentDesignSystemPackageId: packageId,
          updatedAt: now,
        }) as Record<string, unknown>,
        { merge: true },
      );
    }
    await batch.commit();
  }

  async makeDesignSystemPackageCurrent(opts: {
    projectId: string;
    authorId: string;
    platformId: string;
    packageId: string;
  }): Promise<void> {
    const packageId = normalizeDesignSystemPackageId(opts.packageId, 'packageId');
    const packageSnap = await getDoc(
      this.designSystemPackageDocRef(opts.projectId, opts.authorId, opts.platformId, packageId),
    );
    if (!packageSnap.exists()) {
      throw new Error(`Design system package "${packageId}" does not exist.`);
    }
    const profiles = await this.listDesignSystemPackageProfiles({ ...opts, packageId });
    if (profiles.length === 0) {
      throw new Error(`Design system package "${packageId}" has no profiles to materialize.`);
    }
    const packageData = packageSnap.data() as AuthorDesignSystemPackage;
    const now = serverTimestamp() as any;
    const batch = writeBatch(this.db);
    profiles.forEach((profile) => {
      this.materializeDesignSystemPackageProfile(batch, {
        projectId: opts.projectId,
        authorId: opts.authorId,
        platformId: opts.platformId,
        packageId,
        packageName: packageData.name ?? packageId,
        packageContentHash: packageData.contentHash,
        profile,
        now,
      });
    });
    batch.set(
      this.designSystemPackageDocRef(opts.projectId, opts.authorId, opts.platformId, packageId),
      stripUndefined({
        current: true,
        updatedAt: now,
      }) as Record<string, unknown>,
      { merge: true },
    );
    batch.set(
      this.platformDocRef(opts.projectId, opts.authorId, opts.platformId),
      stripUndefined({
        currentDesignSystemPackageId: packageId,
        updatedAt: now,
      }) as Record<string, unknown>,
      { merge: true },
    );
    await batch.commit();
  }

  private materializeDesignSystemPackageProfile(
    batch: ReturnType<typeof writeBatch>,
    opts: {
      projectId: string;
      authorId: string;
      platformId: string;
      packageId: string;
      packageName?: string;
      packageContentHash?: string | null;
      profile: AuthorDesignSystemPackageProfile;
      now: unknown;
    },
  ): void {
    const profileKey = String(opts.profile.profileKey);
    const payload = stripUndefined({
      ...profilePayloadForAuthor(opts.profile, {
        platformId: opts.platformId,
        authorId: opts.authorId,
        profileKey,
        profileId: opts.packageId,
        source: 'package_projection',
      }),
      designPackageId: opts.packageId,
      designPackageName: opts.packageName ?? opts.packageId,
      contentHash: opts.profile.contentHash ?? null,
      designPackageContentHash: opts.packageContentHash ?? null,
      designPackageProfileContentHash: opts.profile.contentHash ?? null,
      updatedAt: opts.now,
    }) as Record<string, unknown>;
    batch.set(
      this.designProfileSetDocRef(opts.projectId, opts.authorId, opts.platformId, profileKey),
      stripUndefined({
        profileKey,
        displayName: getDesignProfileOptionLabel(profileKey),
        description: opts.profile.description ?? opts.profile.designDescription,
        currentProfileId: opts.packageId,
        currentDesignSystemPackageId: opts.packageId,
        isActive: true,
        updatedAt: opts.now,
      }) as Record<string, unknown>,
      { merge: true },
    );
    batch.set(
      this.designProfileDocRef(opts.projectId, opts.authorId, opts.platformId, profileKey, opts.packageId),
      payload,
      { merge: true },
    );
    batch.set(
      this.runtimeDesignDocRef(opts.projectId, opts.authorId, opts.platformId, profileKey),
      payload,
      { merge: true },
    );
  }

  async copySystemDesignProfilesToAuthor(opts: {
    projectId: string;
    authorId: string;
    platformId: string;
    profileKey: string;
    profileIds: string[];
  }): Promise<void> {
    const { projectId, authorId, platformId, profileKey } = opts;
    const profileIds = new Set(opts.profileIds.map((id) => id.trim()).filter(Boolean));
    if (profileIds.size === 0) return;
    const [sets, systemProfiles, existingSetSnap] = await Promise.all([
      systemPlatformService.listBrandStyleKitSets(platformId),
      systemPlatformService.listBrandStyleKitProfiles(platformId, profileKey),
      getDoc(this.designProfileSetDocRef(projectId, authorId, platformId, profileKey)),
    ]);
    const systemSet = sets.find((set) => String(set.profileKey) === profileKey);
    const selectedProfiles = systemProfiles.filter((profile) => profileIds.has(profile.id));
    if (selectedProfiles.length === 0) return;

    const now = serverTimestamp() as any;
    const currentProfileId = String(
      existingSetSnap.exists()
        ? existingSetSnap.data().currentProfileId ?? ''
        : systemSet?.currentProfileId ?? selectedProfiles[0].id,
    ).trim() || selectedProfiles[0].id;
    const batch = writeBatch(this.db);
    batch.set(
      this.designProfileSetDocRef(projectId, authorId, platformId, profileKey),
      stripUndefined({
        ...(systemSet ?? {}),
        profileKey,
        displayName: systemSet?.displayName ?? selectedProfiles[0].name ?? profileKey,
        description: systemSet?.description ?? selectedProfiles[0].description,
        currentProfileId,
        isActive: true,
        createdAt: existingSetSnap.exists() ? existingSetSnap.data().createdAt : now,
        updatedAt: now,
      }) as Record<string, unknown>,
      { merge: true },
    );

    selectedProfiles.forEach((profile) => {
      const payload = profilePayloadForAuthor(profile, {
        platformId,
        authorId,
        profileKey,
        profileId: profile.id,
        source: 'copied_from',
      });
      batch.set(
        this.designProfileDocRef(projectId, authorId, platformId, profileKey, profile.id),
        stripUndefined({
          ...payload,
          createdAt: now,
          updatedAt: now,
        }) as Record<string, unknown>,
        { merge: true },
      );
      if (profile.id === currentProfileId && (!existingSetSnap.exists() || !existingSetSnap.data().currentProfileId)) {
        batch.set(
          this.runtimeDesignDocRef(projectId, authorId, platformId, profileKey),
          stripUndefined({
            ...payload,
            updatedAt: now,
          }) as Record<string, unknown>,
          { merge: true },
        );
      }
    });
    await batch.commit();
  }

  async makeDesignProfileCurrent(opts: {
    projectId: string;
    authorId: string;
    platformId: string;
    profileKey: string;
    profileId: string;
  }): Promise<void> {
    const profileSnap = await getDoc(
      this.designProfileDocRef(opts.projectId, opts.authorId, opts.platformId, opts.profileKey, opts.profileId),
    );
    if (!profileSnap.exists()) {
      throw new Error(`Design profile "${opts.profileId}" does not exist for ${opts.profileKey}.`);
    }
    const now = serverTimestamp() as any;
    const payload = profilePayloadForAuthor(
      { ...(profileSnap.data() as PlatformDesignProfile), id: profileSnap.id } as AuthorDesignProfile,
      {
        platformId: opts.platformId,
        authorId: opts.authorId,
        profileKey: opts.profileKey,
        profileId: opts.profileId,
      },
    );
    const batch = writeBatch(this.db);
    batch.set(
      this.designProfileSetDocRef(opts.projectId, opts.authorId, opts.platformId, opts.profileKey),
      stripUndefined({
        profileKey: opts.profileKey,
        currentProfileId: opts.profileId,
        isActive: true,
        updatedAt: now,
      }) as Record<string, unknown>,
      { merge: true },
    );
    batch.set(
      this.runtimeDesignDocRef(opts.projectId, opts.authorId, opts.platformId, opts.profileKey),
      stripUndefined({
        ...payload,
        updatedAt: now,
      }) as Record<string, unknown>,
      { merge: true },
    );
    await batch.commit();
  }

  async saveDesignProfile(opts: {
    projectId: string;
    authorId: string;
    platformId: string;
    profileKey: string;
    profile: AuthorDesignProfile;
    setCurrent?: boolean;
  }): Promise<void> {
    const profileId = String(opts.profile.id ?? opts.profile.profileId ?? '').trim();
    if (!profileId) throw new Error('profileId is required');
    const now = serverTimestamp() as any;
    const setSnap = await getDoc(
      this.designProfileSetDocRef(opts.projectId, opts.authorId, opts.platformId, opts.profileKey),
    );
    const isCurrent = opts.setCurrent === true || String(setSnap.data()?.currentProfileId ?? '') === profileId;
    const payload = profilePayloadForAuthor(opts.profile, {
      platformId: opts.platformId,
      authorId: opts.authorId,
      profileKey: opts.profileKey,
      profileId,
    });
    const batch = writeBatch(this.db);
    batch.set(
      this.designProfileSetDocRef(opts.projectId, opts.authorId, opts.platformId, opts.profileKey),
      stripUndefined({
        profileKey: opts.profileKey,
        displayName: setSnap.data()?.displayName ?? opts.profile.name ?? opts.profileKey,
        description: setSnap.data()?.description ?? opts.profile.description,
        currentProfileId: isCurrent ? profileId : (setSnap.data()?.currentProfileId ?? profileId),
        isActive: true,
        createdAt: setSnap.exists() ? setSnap.data().createdAt : now,
        updatedAt: now,
      }) as Record<string, unknown>,
      { merge: true },
    );
    batch.set(
      this.designProfileDocRef(opts.projectId, opts.authorId, opts.platformId, opts.profileKey, profileId),
      stripUndefined({
        ...payload,
        updatedAt: now,
      }) as Record<string, unknown>,
      { merge: true },
    );
    if (isCurrent) {
      batch.set(
        this.runtimeDesignDocRef(opts.projectId, opts.authorId, opts.platformId, opts.profileKey),
        stripUndefined({
          ...payload,
          updatedAt: now,
        }) as Record<string, unknown>,
        { merge: true },
      );
    }
    await batch.commit();
  }

  async applyBaseColorsToPostTypes(opts: {
    projectId: string;
    authorId: string;
    platformId: string;
    targetProfileKeys: string[];
  }): Promise<void> {
    const targetProfileKeys = Array.from(new Set(
      opts.targetProfileKeys
        .map((key) => key.trim())
        .filter((key) => key && key !== 'design'),
    ));
    if (targetProfileKeys.length === 0) return;

    const sets = await this.listDesignProfileSets({
      projectId: opts.projectId,
      authorId: opts.authorId,
      platformId: opts.platformId,
      includeProfiles: true,
    });
    const baseSet = sets.find((set) => String(set.profileKey) === 'design');
    const baseCurrentId = String(baseSet?.currentProfileId ?? '').trim();
    const baseProfile = baseSet?.profiles?.find((profile) => profile.id === baseCurrentId)
      ?? baseSet?.profiles?.[0];
    const basePalette = (baseProfile?.designSystem as any)?.palette;
    if (!baseProfile || !basePalette || Object.keys(basePalette).length === 0) {
      throw new Error('Base design profile must have palette colors before applying them to post types.');
    }

    const now = serverTimestamp() as any;
    const batch = writeBatch(this.db);
    targetProfileKeys.forEach((targetProfileKey) => {
      const targetSet = sets.find((set) => String(set.profileKey) === targetProfileKey);
      const targetCurrentId = String(targetSet?.currentProfileId ?? '').trim();
      const targetCurrent = targetSet?.profiles?.find((profile) => profile.id === targetCurrentId)
        ?? targetSet?.profiles?.[0];

      if (!targetCurrent) {
        const profileId = `${targetProfileKey}_from_base_${Date.now()}`;
        const payload = profilePayloadForAuthor(baseProfile, {
          platformId: opts.platformId,
          authorId: opts.authorId,
          profileKey: targetProfileKey,
          profileId,
          source: 'copied_from',
        });
        const createdPayload = stripUndefined({
          ...payload,
          profileId,
          profileKey: targetProfileKey,
          name: `${targetProfileLabel(targetProfileKey)} from ${baseProfile.name ?? 'base colors'}`,
          description: baseProfile.description ?? baseProfile.designDescription,
          createdAt: now,
          updatedAt: now,
        }) as Record<string, unknown>;
        batch.set(
          this.designProfileSetDocRef(opts.projectId, opts.authorId, opts.platformId, targetProfileKey),
          stripUndefined({
            profileKey: targetProfileKey,
            displayName: targetProfileLabel(targetProfileKey),
            description: baseProfile.description ?? baseProfile.designDescription,
            currentProfileId: profileId,
            isActive: true,
            createdAt: now,
            updatedAt: now,
          }) as Record<string, unknown>,
          { merge: true },
        );
        batch.set(
          this.designProfileDocRef(opts.projectId, opts.authorId, opts.platformId, targetProfileKey, profileId),
          createdPayload,
          { merge: true },
        );
        batch.set(
          this.runtimeDesignDocRef(opts.projectId, opts.authorId, opts.platformId, targetProfileKey),
          stripUndefined({
            ...createdPayload,
            createdAt: undefined,
            updatedAt: now,
          }) as Record<string, unknown>,
          { merge: true },
        );
        return;
      }

      const profileId = targetCurrent.id;
      const nextDesignSystem = {
        ...((targetCurrent.designSystem ?? {}) as Record<string, unknown>),
        palette: basePalette,
      };
      const patch = stripUndefined({
        designSystem: nextDesignSystem,
        updatedAt: now,
      }) as Record<string, unknown>;
      batch.set(
        this.designProfileDocRef(opts.projectId, opts.authorId, opts.platformId, targetProfileKey, profileId),
        patch,
        { merge: true },
      );
      batch.set(
        this.runtimeDesignDocRef(opts.projectId, opts.authorId, opts.platformId, targetProfileKey),
        stripUndefined({
          ...targetCurrent,
          id: undefined,
          designSystem: nextDesignSystem,
          profileId,
          profileKey: targetProfileKey,
          platformId: opts.platformId,
          authorId: opts.authorId,
          updatedAt: now,
        }) as Record<string, unknown>,
        { merge: true },
      );
      batch.set(
        this.designProfileSetDocRef(opts.projectId, opts.authorId, opts.platformId, targetProfileKey),
        stripUndefined({
          profileKey: targetProfileKey,
          currentProfileId: profileId,
          isActive: true,
          updatedAt: now,
        }) as Record<string, unknown>,
        { merge: true },
      );
    });
    await batch.commit();
  }
}

function targetProfileLabel(profileKey: string): string {
  return profileKey
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function getDesignProfileOptionLabel(profileKey: string): string {
  return targetProfileLabel(profileKey);
}

const DESIGN_PROFILE_ORDER = [
  'design',
  'single_image_landscape',
  'single_image_square',
  'single_image_portrait',
  'carousel_square',
  'carousel_portrait',
];

function profileKeySortIndex(profileKey: string): number {
  const index = DESIGN_PROFILE_ORDER.indexOf(profileKey);
  return index >= 0 ? index : DESIGN_PROFILE_ORDER.length + 1;
}

function normalizeDesignSystemPackageId(value: unknown, label: string): string {
  const normalized = String(value ?? '').trim();
  if (!normalized) throw new Error(`${label} is required`);
  if (!/^[a-z0-9][a-z0-9_-]*$/.test(normalized)) {
    throw new Error(`${label} must use lowercase letters, numbers, underscores, or hyphens`);
  }
  return normalized;
}

function normalizeDesignSystemPackageProfiles(
  profiles: AuthorDesignSystemPackageProfileInput[],
  packageId: string,
): AuthorDesignSystemPackageProfile[] {
  return profiles
    .map((profile) => {
      const profileKey = normalizeDesignSystemPackageId(profile.profileKey, 'profileKey');
      return {
        ...profile,
        id: profileKey,
        packageId,
        profileKey,
        platformId: String(profile.platformId ?? ''),
        source: String(profile.source ?? 'package_profile') as any,
        profileId: String(profile.profileId ?? packageId),
      } as AuthorDesignSystemPackageProfile;
    });
}

function uniqueStrings(values: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  values.forEach((value) => {
    const normalized = String(value ?? '').trim();
    if (!normalized || seen.has(normalized)) return;
    seen.add(normalized);
    result.push(normalized);
  });
  return result;
}

function profilePayloadForAuthor(
  profile: SystemBrandStyleKitProfile | AuthorDesignProfile | AuthorDesignSystemPackageProfile,
  opts: {
    platformId: string;
    authorId: string;
    profileKey: string;
    profileId: string;
    source?: string;
  },
): PlatformDesignProfile & Record<string, unknown> {
  const payload = { ...(profile as Record<string, unknown>) };
  delete payload.id;
  delete payload.createdAt;
  delete payload.updatedAt;
  return {
    ...payload,
    platformId: opts.platformId,
    authorId: opts.authorId,
    profileId: opts.profileId,
    profileKey: opts.profileKey,
    source: opts.source ?? (profile as any).source ?? 'copied_from',
    copiedFromPlatformId: String((profile as any).copiedFromPlatformId ?? opts.platformId),
    copiedFromProfileId: String((profile as any).copiedFromProfileId ?? (profile as any).profileId ?? opts.profileId),
    copiedFromProfileKey: String((profile as any).copiedFromProfileKey ?? opts.profileKey),
    isComplete: (profile as any).isComplete ?? true,
  } as PlatformDesignProfile & Record<string, unknown>;
}

export const authorPlatformService = new AuthorPlatformService('authors');
export const externalAuthorPlatformService = new AuthorPlatformService('external_authors');

/** Pick the right singleton given a basePath. Falls back to self-author. */
export function getAuthorPlatformService(
  basePath?: AuthorBasePath,
): AuthorPlatformService {
  return basePath === 'external_authors'
    ? externalAuthorPlatformService
    : authorPlatformService;
}

export { AuthorPlatformService };
